import React, { useState, useEffect, useMemo } from "react";
import { api } from "@/lib/api";
import { PageTitle, EmptyState, ConfirmDelete } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Link2 as LinkIcon, Plus, Copy, Trash2, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

const DURATIONS = [
  { value: "0", label: "Tanpa batas" },
  { value: "1", label: "1 hari" },
  { value: "7", label: "7 hari" },
  { value: "30", label: "30 hari" },
];

function fmtDateTime(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("id-ID", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function RegLinks() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [duration, setDuration] = useState("7");
  const [note, setNote] = useState("");
  const [plan, setPlan] = useState("premium");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/reg-links"); setLinks(data); }
    catch { toast.error("Gagal memuat link"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const linkUrl = (token) => `${window.location.origin}/daftar/${token}`;

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/reg-links", { duration_days: parseInt(duration, 10), note, plan });
      setLinks((prev) => [data, ...prev]);
      setNote("");
      try { await navigator.clipboard.writeText(linkUrl(data.token)); } catch { /* ignore */ }
      toast.success("Link pendaftaran baru dibuat & disalin");
    } catch { toast.error("Gagal membuat link"); }
    finally { setGenerating(false); }
  };

  const copyLink = async (token) => {
    try { await navigator.clipboard.writeText(linkUrl(token)); toast.success("Link disalin!"); }
    catch { toast.info(linkUrl(token)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/reg-links/${id}`); setLinks((prev) => prev.filter((l) => l.id !== id)); toast.success("Link dihapus"); }
    catch { toast.error("Gagal menghapus link"); }
  };

  const setNoteLocal = (id, value) => setLinks((prev) => prev.map((l) => (l.id === id ? { ...l, note: value } : l)));
  const saveNote = async (id, value) => {
    try { await api.put(`/reg-links/${id}`, { note: value }); }
    catch { toast.error("Gagal menyimpan catatan"); }
  };

  const activeCount = useMemo(() => links.filter((l) => !l.used && !l.expired).length, [links]);

  const statusBadge = (l) => {
    if (l.used) return <Badge variant="outline" className="gap-1 text-muted-foreground"><CheckCircle2 className="h-3 w-3" />Terpakai</Badge>;
    if (l.expired) return <Badge variant="outline" className="gap-1 text-amber-500"><Clock className="h-3 w-3" />Kedaluwarsa</Badge>;
    return <Badge className="gap-1 bg-green-600 hover:bg-green-600" data-testid={`status-active-${l.id}`}>Aktif</Badge>;
  };

  return (
    <div>
      <PageTitle title="Link Pendaftaran" subtitle="Kelola link pendaftaran sekali pakai (sysadmin & admin)" />

      <Card className="mb-4 border-primary/40 bg-primary/5 p-4">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">Link Pendaftaran Sekali Pakai</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Setiap link hanya bisa dipakai untuk <b>satu</b> pendaftaran, lalu otomatis nonaktif. Link aktif: <b data-testid="active-count">{activeCount}</b>
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div className="w-full sm:w-auto sm:flex-1">
            <label className="text-xs font-medium text-muted-foreground">Catatan / Label (opsional)</label>
            <Input data-testid="gen-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="mis. Untuk keluarga Budi" className="mt-1" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Masa Berlaku</label>
            <Select value={duration} onValueChange={setDuration}>
              <SelectTrigger data-testid="gen-duration-select" className="mt-1 w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DURATIONS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Paket</label>
            <Select value={plan} onValueChange={setPlan}>
              <SelectTrigger data-testid="gen-plan-select" className="mt-1 w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="bulan1">Paket 1 Bulan</SelectItem>
                <SelectItem value="bulan6">Paket 6 Bulan</SelectItem>
                <SelectItem value="premium">Paket Premium</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="generate-link-btn" onClick={generate} disabled={generating} className="gap-2 rounded-full">
            <Plus className="h-4 w-4" /> {generating ? "Membuat..." : "Generate Link Baru"}
          </Button>
        </div>
      </Card>

      {loading ? (
        <p className="py-10 text-center text-sm text-muted-foreground">Memuat…</p>
      ) : links.length === 0 ? (
        <EmptyState text="Belum ada link. Klik 'Generate Link Baru' untuk membuat link pendaftaran." />
      ) : (
        <div className="rounded-xl border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Link Pendaftaran</TableHead>
                <TableHead>Paket</TableHead>
                <TableHead>Catatan</TableHead>
                <TableHead>Dibuat</TableHead>
                <TableHead>Kedaluwarsa</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Dipakai Oleh</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {links.map((l) => (
                <TableRow key={l.id} data-testid={`reg-link-row-${l.id}`}>
                  <TableCell className="max-w-[260px]">
                    <span className="block break-all font-mono text-xs">{linkUrl(l.token)}</span>
                  </TableCell>
                  <TableCell><Badge variant="outline" className="text-xs" data-testid={`link-plan-${l.id}`}>{l.plan === "bulan1" ? "1 Bulan" : l.plan === "bulan6" ? "6 Bulan" : "Premium"}</Badge></TableCell>
                  <TableCell className="min-w-[160px]">
                    <Input
                      data-testid={`note-input-${l.id}`}
                      value={l.note || ""}
                      onChange={(e) => setNoteLocal(l.id, e.target.value)}
                      onBlur={(e) => saveNote(l.id, e.target.value)}
                      placeholder="Tambah catatan…"
                      className="h-8 text-xs"
                      disabled={l.used}
                    />
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm text-muted-foreground">{fmtDateTime(l.created_at)}</TableCell>
                  <TableCell className="whitespace-nowrap text-sm text-muted-foreground">{l.expires_at ? fmtDateTime(l.expires_at) : "Tanpa batas"}</TableCell>
                  <TableCell>{statusBadge(l)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{l.used_by || "-"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {!l.used && !l.expired && (
                        <Button data-testid={`copy-link-${l.id}`} onClick={() => copyLink(l.token)} size="icon" variant="ghost" title="Salin link">
                          <Copy className="h-4 w-4" />
                        </Button>
                      )}
                      <ConfirmDelete
                        testId={`delete-link-${l.id}`}
                        title="Hapus link pendaftaran?"
                        description="Link ini akan dihapus permanen dan tidak bisa dipakai lagi."
                        onConfirm={() => remove(l.id)}
                        trigger={
                          <Button data-testid={`delete-link-${l.id}`} size="icon" variant="ghost" title="Hapus link" className="text-red-500 hover:text-red-600">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        }
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
